/**
 * Фото товара с увеличением по наведению — общий на кабинет.
 *
 * Жил в `ProfitPage` с 08.09.26 и вынесен 15.09.26, когда тот же приём
 * понадобился «Планам» (замечание владельца: название товара ценности не
 * несёт, фото при наведении увеличивать). Вторая копия разошлась бы с первой
 * на первой же правке размера или слоя.
 *
 * Правила переехали вместе с кодом, и все они куплены:
 *
 * - увеличенная копия рисуется НЕ в ячейке, а слоем поверх страницы, и она
 *   `fixed`, а не `absolute`: таблица лежит в своём scroll-контейнере с
 *   `overflow: auto`, и абсолютный блок обрезался бы по его границе;
 * - состояние наведения живёт у ВЛАДЕЛЬЦА таблицы, а не в строке: виртуализация
 *   переиспользует узлы строк, и при прокрутке оно досталось бы чужому товару;
 * - координаты берутся у самой ячейки, а не у курсора: картинка, дрожащая под
 *   мышью, читается хуже неподвижной;
 * - у слоя `pointer-events-none`: иначе картинка накрыла бы курсор, увела мышь
 *   с фото и погасила сама себя.
 */

/** Что показывает слой увеличения: картинка и место, откуда её позвали. */
export type Увеличение = { src: string; x: number; y: number };

export function Фото({
  src,
  onShow,
  title,
  size = "h-[24px] w-[18px]",
  rounded = "rounded-[2px]",
}: {
  src: string | null;
  onShow: (готово: Увеличение | null) => void;
  /** Подсказка: на «Планах» в ней название и артикул, ушедшие из строки. */
  title?: string | undefined;
  /** Классы размера. Строка «Прибыли» ниже строки «Планов», и фото у них разное. */
  size?: string;
  /**
   * Скругление и рамка. Отдельным свойством, а не частью `size`: обе строки
   * задают радиус, и две одинаковые по весу произвольные утилиты Tailwind
   * разошлись бы не по порядку в строке класса, а по порядку в собранном CSS —
   * то есть непредсказуемо. День приёмки просит 4px с рамкой, таблицы — 2px.
   */
  rounded?: string;
}) {
  if (!src)
    return <span title={title} className={`${size} ${rounded} flex-none bg-bg-subtle`} />;
  return (
    <img
      src={src}
      alt=""
      title={title}
      loading="lazy"
      className={`${size} ${rounded} flex-none object-cover`}
      onMouseEnter={(event) => {
        const место = event.currentTarget.getBoundingClientRect();
        onShow({ src, x: место.right + 8, y: место.top });
      }}
      onMouseLeave={() => onShow(null)}
    />
  );
}

/** Слой увеличения — ОДИН на страницу. */
export function СлойУвеличения({ value }: { value: Увеличение | null }) {
  if (!value) return null;
  return (
    <img
      src={value.src}
      alt=""
      className="pointer-events-none fixed z-50 h-[320px] w-[240px] rounded-[var(--radius-card)] border border-border bg-bg object-cover shadow-lg"
      style={{
        left: value.x,
        // Не даём картинке уехать за нижнюю кромку окна.
        top: Math.min(value.y, window.innerHeight - 330),
      }}
    />
  );
}
